/*
 * SubscriptionPage.tsx (AbstractECommerce)
 *
 * Copyright © 2023 InstaLOD GmbH - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * This file and all its contents are proprietary and confidential.
 *
 * Maintained by Rafael Rodrigues, 2023
 *
 * @file SubscriptionPage.tsx
 * @author Rafael Rodrigues
 * @copyright 2023 InstaLOD GmbH. All rights reserved.
 * @section License
 */

import React, { useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { useDispatch, useSelector } from 'react-redux';
import { getAllSubscriptionsAction } from '../../../Store/Subscriptions/AdminSubscriptions';
import SubscriptionTable from './SubscriptionsTable';
import {
  IPageEvent,
  ISortEvent,
  ITablePayload
} from '@abstract/abstractwebcommon-shared/interfaces/pagination';
import { defaultTableLimit } from '@abstract/abstractwebcommon-client/Constants';
import {
  cancelSubscriptionAction,
  getSubscriptionPaymentMethodAction,
  refundSubscriptionAction,
  resetPaymentMethodState,
  updateSubscriptionPaymentMethodAction,
  retrySubscriptionAction
} from '../../../Store/Subscriptions/SharedSubscriptions';
import { asyncErrorHandler } from '@abstract/abstractwebcommon-shared/utils/AsyncErrorHandler';

const SubscriptionPage = () => {
  const dispatch = useDispatch();
  const subscriptions = useSelector((state) => state.adminSubscriptions);
  const isLoading = useSelector(
    (state) => state.adminSubscriptions.isFetchingList
  );
  const [tablePayload, setPayload] = useState<ITablePayload>({
    limit: defaultTableLimit,
    skip: 0,
    sort: { sortField: 'createdAt', sortOrder: -1 },
    searchTerm: ''
  }); /**< Default Payload */

  const handlePageUpdate = (event: IPageEvent): void => {
    const updatedPayload: ITablePayload = {
      skip: event.first,
      limit: event.rows,
      sort: {
        sortField: tablePayload.sort.sortField,
        sortOrder: tablePayload.sort.sortOrder
      },
      searchTerm: tablePayload.searchTerm,
      filter: tablePayload.filter
    };
    setPayload(updatedPayload);
    dispatch(
      getAllSubscriptionsAction(
        updatedPayload.skip,
        updatedPayload.limit,
        updatedPayload.searchTerm,
        updatedPayload.sort,
        updatedPayload.filter
      )
    );
  };

  /// Handle sort update event
  const handleSortUpdate = (event: ISortEvent): void => {
    const updatedPayload: ITablePayload = tablePayload; /**< Updated Payload. */
    Object.assign(updatedPayload, {
      sort: event
    });
    setPayload(updatedPayload);
    dispatch(
      getAllSubscriptionsAction(
        updatedPayload.skip,
        updatedPayload.limit,
        updatedPayload.searchTerm,
        updatedPayload.sort,
        updatedPayload.filter
      )
    );
  };

  /// Handle filter update event
  const handleFilterUpdate = (event: string): void => {
    const updatedPayload: ITablePayload = tablePayload; /**< Updated Payload. */
    Object.assign(updatedPayload, { searchTerm: event });
    setPayload(updatedPayload);
    dispatch(
      getAllSubscriptionsAction(
        updatedPayload.skip,
        updatedPayload.limit,
        updatedPayload.searchTerm,
        updatedPayload.sort,
        updatedPayload.filter
      )
    );
  };

  /// Refund a subscription and fetch subscription list again.
  const refundSubscription = (subscriptionID: string) => {
    dispatch(
      refundSubscriptionAction(subscriptionID, subscriptions.list, true)
    );
  };

  /// Cancel a subscription and fetch subscription list again.
  const cancelSubscription = async (subscriptionID: string) => {
    dispatch(
      cancelSubscriptionAction(subscriptionID, subscriptions.list, true)
    );
  };

  // Retry a subscription and fetch subscription list again.
  const retrySubscription = async (
    subscriptionID: string,
    transactionPrice?: string /* The Production environment won't pass the transaction price. */
  ) => {
    dispatch(retrySubscriptionAction(subscriptionID, transactionPrice, true));
  };

  useEffect(() => {
    dispatch(
      getAllSubscriptionsAction(
        tablePayload.skip,
        tablePayload.limit,
        tablePayload.searchTerm,
        tablePayload.sort,
        tablePayload.filter
      )
    ); /**< Fetch all subscriptions. */
  }, []);

  /// Update handler for subscription payment method
  const handleSubscriptionPaymentMethodUpdate = async (
    subscriptionID: string,
    paymentMethodNonce: string
  ) => {
    await asyncErrorHandler(
      dispatch(
        updateSubscriptionPaymentMethodAction(
          subscriptionID,
          paymentMethodNonce
        )
      )
    );
    /**< To update subscription's payment method */
    await asyncErrorHandler(
      dispatch(
        getAllSubscriptionsAction(
          tablePayload.skip,
          tablePayload.limit,
          tablePayload.searchTerm,
          tablePayload.sort,
          tablePayload.filter
        )
      )
    ); /**< To fetch all subscriptions. */
  };

  /// To reset payment method
  const resetPaymentMethod = async () => {
    await asyncErrorHandler(dispatch(resetPaymentMethodState()));
  };

  /// To get subscription payment method
  const getSubscriptionPaymentMethod = async (subscriptionID: string) => {
    await asyncErrorHandler(
      dispatch(getSubscriptionPaymentMethodAction(subscriptionID))
    );
  };

  // To terminate subscription
  const onTerminateSubscription = async (subscriptionID: string) => {
    dispatch(
      cancelSubscriptionAction(subscriptionID, subscriptions.list, true, true)
    );
  };

  // Handle product filter update event
  const handleProductFilterUpdate = (updatedPayload: ITablePayload): void => {
    setPayload(updatedPayload);
    dispatch(
      getAllSubscriptionsAction(
        updatedPayload.skip,
        updatedPayload.limit,
        updatedPayload.searchTerm,
        updatedPayload.sort,
        updatedPayload.filter
      )
    );
  };

  return (
    <Row>
      <Col sm={12}>
        <SubscriptionTable
          refundSubscription={refundSubscription}
          cancelSubscription={cancelSubscription}
          retrySubscription={retrySubscription}
          subscriptions={subscriptions.list}
          isLoading={isLoading}
          handlePageUpdate={handlePageUpdate}
          handleSortUpdate={handleSortUpdate}
          handleFilterUpdate={handleFilterUpdate}
          tablePayload={tablePayload}
          totalRecords={subscriptions.totalRecords}
          handleSubscriptionPaymentMethodUpdate={
            handleSubscriptionPaymentMethodUpdate
          }
          resetPaymentMethod={resetPaymentMethod}
          getSubscriptionPaymentMethod={getSubscriptionPaymentMethod}
          onTerminateSubscription={onTerminateSubscription}
          handleProductFilterUpdate={handleProductFilterUpdate}
        />
      </Col>
    </Row>
  );
};

export default SubscriptionPage;
